import { createContext, useContext, type ReactNode } from 'react';
import { assert } from '~/lib/assert';
import type { EMPTY_REPORT } from '../lib';
import type {
  PlayoutReport__Report,
  PlayoutReportVariables,
} from '../queries/PlayoutReport.generated';

// Layout-owned state shared with the routed tabs. The layout runs the main
// PlayoutReport query, then exposes the report data (real or EMPTY_REPORT),
// the filter variables that query is using (so tabs that fire their own
// queries respect the page-level filter), and whether the filter selection
// is incomplete (so tabs can render an empty state instead of querying).
export interface PlayoutReportsContextType {
  report: PlayoutReport__Report | typeof EMPTY_REPORT;
  variables: PlayoutReportVariables;
  querySkipped: boolean;
}

const PlayoutReportsContext = createContext<PlayoutReportsContextType | undefined>(undefined);

export const usePlayoutReport = () => {
  const context = useContext(PlayoutReportsContext);
  assert(context !== undefined, 'usePlayoutReport must be used within a PlayoutReportsProvider');
  return context;
};

export interface PlayoutReportsProviderProps extends PlayoutReportsContextType {
  children: ReactNode;
}

export const PlayoutReportsProvider = ({
  children,
  report,
  variables,
  querySkipped,
}: PlayoutReportsProviderProps) => (
  <PlayoutReportsContext.Provider value={{ report, variables, querySkipped }}>
    {children}
  </PlayoutReportsContext.Provider>
);
